/* eslint-disable react/no-danger */
import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Collapse, Button, Tooltip, message } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPlus,
	faTrash,
	faFileCode,
	faDownload,
} from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import {
	BacnetConfigEntryInterface,
	BacnetLn2Mapping,
} from "../../../../../interfaces/AdapterCdoBacnet.interface";
import { RootState } from "../../../../../app/store";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import FormSelectMessage from "../../../../components/form/FormSelectMessage";
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import SelectLifts from "../../../../components/select/SelectLifts";
// CONSTANTS
import {
	DEFAULT_BACNET_LN2_MAPPING,
} from "../../../../../constants/adapter";

// ============================================================
// Normalization helpers
// ============================================================

/**
 * Normalize a single ln2_msg from JSON.
 * The JSON stores a ValuedMessage: { msg_id, position, active }
 * The UI expands it to a FrontEndMessage: { bank_id, lift_id, msg: { ... } }
 */
export function normalizeLn2Msg(raw: any): BacnetLn2Mapping["ln2_msg"] {
	if (raw && raw.msg && typeof raw.msg === "object") {
		// Already in nested format — keep msg, ignore bank_id/lift_id
		return { bank_id: "", lift_id: "", msg: raw.msg };
	}
	// Flat ValuedMessage format — wrap into nested
	return {
		bank_id: "",
		lift_id: "",
		msg: {
			msg_id: raw?.msg_id || "",
			position: raw?.position ?? 1,
			active: raw?.active ?? true,
		},
	};
}

export default function CdoBacnetPage({
	setCdo,
	cdoValue,
	bankId,
}: {
	setCdo: React.Dispatch<React.SetStateAction<BacnetConfigEntryInterface>>;
	cdoValue: BacnetConfigEntryInterface;
	bankId: string;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { theme } = useSelector((state: RootState) => state.home);
	const { Panel } = Collapse;
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [description, setDescription] = useState<BacnetLn2Mapping>({
		...DEFAULT_BACNET_LN2_MAPPING,
	});
	const [useCurrentBank, setUseCurrentBank] = useState<boolean>(true);
	const [importStats, setImportStats] = useState<{ total: number; valid: number; invalid: number } | null>(null);
	const [invalidDescriptions, setInvalidDescriptions] = useState<Array<{ description: any; reasons: string[] }>>([]);

	// ************************************************ */
	// USE EFFECT ************************************* */
	// Reset states when drawer is closed (cdoValue is reset to default)
	React.useEffect(() => {
		if (cdoValue.descriptions.length === 0 && cdoValue.bank_id === "") {
			setDescription({ ...DEFAULT_BACNET_LN2_MAPPING });
			setImportStats(null);
			setInvalidDescriptions([]);
			setUseCurrentBank(true);
		}
	}, [cdoValue]);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const formatJsonWithColors = (obj: any) => {
		const json = JSON.stringify(obj, null, 4)
			.replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
			.replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
			.replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
		return json;
	};

	// Validate if description has all required fields filled
	const isDescriptionValid = React.useMemo(() => {
		const point = description.point;
		const ln2_msg = description.ln2_msg;

		// Check all required point fields
		if (!point.host_name || point.host_name.trim() === "") return false;
		if (!point.host_id || point.host_id.trim() === "") return false;
		if (!point.lift_id || point.lift_id.trim() === "") return false;
		if (!point.lift_name || point.lift_name.trim() === "") return false;
		if (!point.group_name || point.group_name.trim() === "") return false;
		if (!point.group_id || point.group_id.trim() === "") return false;
		if (!point.point_name || point.point_name.trim() === "") return false;
		if (point.point_id === undefined || point.point_id === null) return false;
		if (point.value === undefined || point.value === null) return false;

		// Check all required ln2_msg fields
		if (!ln2_msg.msg.msg_id || ln2_msg.msg.msg_id.trim() === "") return false;
		if (ln2_msg.msg.position === undefined || ln2_msg.msg.position === null) return false;
		if (ln2_msg.msg.active === undefined || ln2_msg.msg.active === null) return false;

		return true;
	}, [description]);

	const onChangeFormValueDescription = (
		newValue: string | number | boolean,
		variableName: string,
	) => {
		const copy: BacnetLn2Mapping = structuredClone(description);
		if (variableName === "host_name" && typeof newValue === "string") {
			copy.point.host_name = newValue;
		} else if (variableName === "point_name" && typeof newValue === "string") {
			copy.point.point_name = newValue;
		} else if (variableName === "group_name" && typeof newValue === "string") {
			copy.point.group_name = newValue;
		} else if (variableName === "host_id" && typeof newValue === "string") {
			copy.point.host_id = newValue;
		} else if (variableName === "group_id" && typeof newValue === "string") {
			copy.point.group_id = newValue;
		} else if (variableName === "bank_id" && typeof newValue === "string") {
			copy.point.bank_id = newValue;
		} else if (variableName === "bank_name" && typeof newValue === "string") {
			copy.point.bank_name = newValue;
		} else if (variableName === "lift_id" && typeof newValue === "string") {
			copy.point.lift_id = newValue;
		} else if (variableName === "lift_name" && typeof newValue === "string") {
			copy.point.lift_name = newValue;
		} else if (variableName === "value" && typeof newValue === "number") {
			copy.point.value = newValue;
		} else if (variableName === "point_id" && typeof newValue === "number") {
			copy.point.point_id = newValue;
		} else if (variableName === "msg_id" && typeof newValue === "string") {
			copy.ln2_msg.msg.msg_id = newValue;
		} else if (variableName === "active" && typeof newValue === "boolean") {
			copy.ln2_msg.msg.active = newValue;
		} else if (variableName === "position" && typeof newValue === "number") {
			copy.ln2_msg.msg.position = newValue;
		}
		setDescription(copy);
	};

	const onClickAddNewDescription = () => {
		const newDescription: BacnetLn2Mapping = {
			...description,
			ln2_msg: {
				...description.ln2_msg,
				bank_id: bankId,
			},
		};
		setCdo((prev: BacnetConfigEntryInterface) => ({
			...prev,
			bank_id: bankId,
			descriptions: [...prev.descriptions, newDescription],
		}));
		setDescription({ ...DEFAULT_BACNET_LN2_MAPPING });
	};

	// Bank ID is now automatically set from the parent component
	// const onChangeBank = (newValue: string) => {
	// 	setCdo((prev: BacnetConfigEntryInterface) => ({
	// 		...prev,
	// 		bank_id: newValue,
	// 	}));
	// };

	const onChangeLift = (liftId: string, liftName: string) => {
		setDescription((prev: BacnetLn2Mapping) => ({
			...prev,
			point: {
				...prev.point,
				lift_id: liftId,
				lift_name: liftName,
				bank_id: bankId,
			},
			ln2_msg: {
				...prev.ln2_msg,
				lift_id: liftId,
				bank_id: bankId,
			},
		}));
	};


	const onClickRemoveDescription = (index: number) => {
		setCdo((prev: BacnetConfigEntryInterface) => ({
			...prev,
			descriptions: prev.descriptions.filter((_, i) => i !== index),
		}));
	};

	function uploadJSONFile(
		onLoadCallback: (data: any, fileName: string) => void,
	) {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "application/json";
		input.onchange = (event: any) => {
			const file = event.target.files?.[0];
			if (!file) return;
			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const text: any = e.target?.result;
					const data = JSON.parse(text);
					onLoadCallback(data, file.name);
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error(error);
				} finally {
					input.value = "";
				}
			};

			reader.readAsText(file);
		};

		input.click();
	}

	const validateBacnetLn2Mapping = (item: any): { isValid: boolean; reasons: string[] } => {
		const reasons: string[] = [];

		if (!item || typeof item !== "object") {
			reasons.push("Item is null, undefined, or not an object");
			return { isValid: false, reasons };
		}

		// Validate point structure
		if (!item.point || typeof item.point !== "object") {
			reasons.push("Missing or invalid 'point' object");
			return { isValid: false, reasons };
		}

		const point = item.point;
		if (typeof point.host_id !== "string") reasons.push("point.host_id must be a string");
		if (typeof point.host_name !== "string") reasons.push("point.host_name must be a string");
		if (typeof point.bank_id !== "string") reasons.push("point.bank_id must be a string");
		if (typeof point.bank_name !== "string") reasons.push("point.bank_name must be a string");
		if (typeof point.lift_id !== "string") reasons.push("point.lift_id must be a string");
		if (typeof point.lift_name !== "string") reasons.push("point.lift_name must be a string");
		if (typeof point.group_name !== "string") reasons.push("point.group_name must be a string");
		if (typeof point.group_id !== "string") reasons.push("point.group_id must be a string");
		if (typeof point.point_name !== "string") reasons.push("point.point_name must be a string");
		if (typeof point.point_id !== "number" && typeof point.point_id !== "string") {
			reasons.push("point.point_id must be a number or string");
		}
		if (typeof point.value !== "number" && typeof point.value !== "string") {
			reasons.push("point.value must be a number or string");
		}

		// Validate ln2_msg structure
		if (!item.ln2_msg || typeof item.ln2_msg !== "object") {
			reasons.push("Missing or invalid 'ln2_msg' object");
			return { isValid: false, reasons };
		}

		const ln2_msg = item.ln2_msg;
		if (typeof ln2_msg.bank_id !== "string") reasons.push("ln2_msg.bank_id must be a string");
		if (typeof ln2_msg.lift_id !== "string") reasons.push("ln2_msg.lift_id must be a string");

		if (!ln2_msg.msg || typeof ln2_msg.msg !== "object") {
			reasons.push("Missing or invalid 'ln2_msg.msg' object");
		} else {
			if (typeof ln2_msg.msg.msg_id !== "string") reasons.push("ln2_msg.msg.msg_id must be a string");
			if (typeof ln2_msg.msg.position !== "number" && typeof ln2_msg.msg.position !== "string") {
				reasons.push("ln2_msg.msg.position must be a number or string");
			}
			if (typeof ln2_msg.msg.active !== "boolean") reasons.push("ln2_msg.msg.active must be a boolean");
		}

		return { isValid: reasons.length === 0, reasons };
	};

	const extractDescriptionsFromJson = (data: any, useCurrentBankId: boolean): { descriptions: BacnetLn2Mapping[], totalFound: number, validCount: number, invalidItems: Array<{ description: any; reasons: string[] }> } => {
		let allDescriptions: any[] = [];

		// Try to find descriptions in different possible locations
		if (Array.isArray(data.descriptions)) {
			allDescriptions = data.descriptions;
		} else if (data.bacnet_config && Array.isArray(data.bacnet_config)) {
			// Look for descriptions in bacnet_config array
			data.bacnet_config.forEach((config: any) => {
				if (config.entries && Array.isArray(config.entries)) {
					config.entries.forEach((entry: any) => {
						if (Array.isArray(entry.descriptions)) {
							allDescriptions = [...allDescriptions, ...entry.descriptions];
						}
					});
				}
			});
		} else if (data.entries && Array.isArray(data.entries)) {
			// Look for descriptions in entries array
			data.entries.forEach((entry: any) => {
				if (Array.isArray(entry.descriptions)) {
					allDescriptions = [...allDescriptions, ...entry.descriptions];
				}
			});
		}

		const totalFound = allDescriptions.length;
		const validDescriptions: BacnetLn2Mapping[] = [];
		const invalidItems: Array<{ description: any; reasons: string[] }> = [];

		// Validate and filter descriptions
		allDescriptions.forEach((desc: any, index: number) => {
			const validation = validateBacnetLn2Mapping(desc);

			if (validation.isValid) {
				// Normalize the description to ensure correct types
				const normalizedDesc: BacnetLn2Mapping = {
					point: {
						host_id: String(desc.point.host_id),
						host_name: String(desc.point.host_name),
						bank_id: String(desc.point.bank_id),
						bank_name: String(desc.point.bank_name),
						lift_id: String(desc.point.lift_id),
						lift_name: String(desc.point.lift_name),
						group_name: String(desc.point.group_name),
						group_id: String(desc.point.group_id),
						point_id: Number(desc.point.point_id),
						point_name: String(desc.point.point_name),
						value: Number(desc.point.value),
					},
					ln2_msg: {
						bank_id: useCurrentBankId ? bankId : String(desc.ln2_msg.bank_id),
						lift_id: String(desc.ln2_msg.lift_id),
						msg: {
							msg_id: String(desc.ln2_msg.msg.msg_id),
							position: Number(desc.ln2_msg.msg.position),
							active: Boolean(desc.ln2_msg.msg.active),
						},
					},
				};
				validDescriptions.push(normalizedDesc);
			} else {
				invalidItems.push({
					description: { ...desc, originalIndex: index },
					reasons: validation.reasons,
				});
			}
		});

		return {
			descriptions: validDescriptions,
			totalFound,
			validCount: validDescriptions.length,
			invalidItems,
		};
	};

	const onClickUploadJsonFile = () => {
		uploadJSONFile((data) => {
			if (data && typeof data === "object") {
				const { descriptions, totalFound, validCount, invalidItems } = extractDescriptionsFromJson(data, useCurrentBank);

				// Update the configuration with valid descriptions
				const configData: BacnetConfigEntryInterface = {
					bank: data.bank || {},
					bank_id: bankId,
					descriptions,
				};
				setCdo(configData);

				// Save statistics and invalid descriptions
				setImportStats({
					total: totalFound,
					valid: validCount,
					invalid: totalFound - validCount,
				});
				setInvalidDescriptions(invalidItems);

				// Show statistics message
				if (totalFound === 0) {
					message.warning("No descriptions found in the uploaded file.");
				} else if (validCount === 0) {
					message.error(`Found ${totalFound} descriptions but none were valid.`);
				} else if (validCount < totalFound) {
					message.warning(`Imported ${validCount} valid descriptions out of ${totalFound} found. ${totalFound - validCount} were invalid.`);
				} else {
					message.success(`Successfully imported ${validCount} descriptions.`);
				}
			}
			setDescription({ ...DEFAULT_BACNET_LN2_MAPPING });
		});
	};

	const onClickDownloadJsonFile = () => {
		const blob = new Blob([JSON.stringify(cdoValue, null, 4)], {
			type: "application/json",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "bacnet-config.json";
		a.click();
		URL.revokeObjectURL(url);
	};


	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<DrawerRowDivider icon={faFileCode} title='BACNET' />
			{/* ======== JSON Preview ======== */}
			<Collapse>
				<Panel header={`Bacnet JSON ${t("general.preview")}`} key='1'>
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<pre
								style={{
									width: "100%",
									color: "#dcdcdc",
									padding: "10px",
									borderRadius: "8px",
									fontSize: "14px",
									overflowX: "auto",
								}}
								dangerouslySetInnerHTML={{
									__html: formatJsonWithColors(cdoValue),
								}}
							/>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
			{/* ======== JSON DESCRIPTIONS ======== */}
			<Collapse>
				<Panel header={t("general.descriptions")} key='2'>
					<div className='container'>
						<div className="row">
							<div className='generalStyles__width100'>
								{cdoValue.descriptions.map(
									(item: BacnetLn2Mapping, itemIndex: number) => (
										<div
											key={itemIndex}
											style={{
												borderRadius: "5px",
												fontSize: "9px",
												padding: "15px",
											}}
										>
											<div style={{ marginBottom: "10px" }}>
												<span style={{ color: '#4ec9b0', fontWeight: 'bold', fontSize: '12px' }}>
													Entry #{itemIndex + 1}
												</span>
											</div>
											<div
												className={`drawer__box__${theme} mt-3`}
												style={{
													width: "100%",
												}}
											>
												<pre
													style={{
														width: "100%",
														padding: "10px",
														borderRadius: "8px",
														overflowX: "auto",
													}}
													dangerouslySetInnerHTML={{
														__html: formatJsonWithColors(item),
													}}
												/>
												<div className='generalStyles__flexCenter'>
													<Tooltip title={t("general.delete")}>
														<Button
															onClick={() => {
																onClickRemoveDescription(itemIndex);
															}}
															className='buttonStyle__29'
															shape='circle'
															icon={<FontAwesomeIcon icon={faTrash} />}
														/>
													</Tooltip>
												</div>
											</div>
										</div>
									),
								)}
							</div>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
			{/* ======== INVALID DESCRIPTIONS ======== */}
			{invalidDescriptions.length > 0 && (
				<>
					<Collapse>
						<Panel
							header={
								<span style={{ color: '#f48771' }}>
									{t("general.error")} ({invalidDescriptions.length})
								</span>
							}
							key='3'
						>
							<div className='container'>
								<div className="row">
									<div className='generalStyles__width100'>
										{invalidDescriptions.map((item, itemIndex: number) => (
											<div
												key={itemIndex}
												className='mb-3'
												style={{
													borderRadius: "5px",
													border: "1px solid #f48771",
													padding: "15px",
												}}
											>
												<div style={{ marginBottom: "10px" }}>
													<span style={{ color: '#f48771', fontWeight: 'bold', fontSize: '12px' }}>
														Entry #{item.description.originalIndex + 1}
													</span>
												</div>

												{/* Validation Errors */}
												<div className={`drawer__box__${theme} mt-3`}
													style={{ marginBottom: "15px" }}>
													<p style={{ color: '#9cdcfe', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>
														Validation Errors:
													</p>
													<ul style={{ margin: 0, paddingLeft: '20px' }}>
														{item.reasons.map((reason: string, reasonIndex: number) => (
															<li key={reasonIndex} style={{ color: '#f48771', fontSize: '10px', marginBottom: '4px' }}>
																{reason}
															</li>
														))}
													</ul>
												</div>

												{/* Raw JSON Data */}
												<div>
													<p style={{ color: '#9cdcfe', fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>
														Raw Data:
													</p>
													<div className={`drawer__box__${theme}`}>
														<pre
															style={{
																width: "100%",
																color: "#dcdcdc",
																padding: "10px",
																borderRadius: "8px",
																fontSize: "9px",
																overflowX: "auto",
															}}
															dangerouslySetInnerHTML={{
																__html: formatJsonWithColors(item.description),
															}}
														/>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</Panel>
					</Collapse>
					<br />
				</>
			)}
			{/* ======== Formulary ======== */}
			<Collapse>
				<Panel header={`Bacnet ${t("general.formulary")}`} key='4'>
					{/* ======== BACnet Point Description ======== */}
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<div className='col-md-12'>
								<span className='drawer__text' style={{ fontWeight: "bold" }}>
									BACnet Point
								</span>
							</div>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										<span className='drawer__text'>Lift</span>
									</div>
									<div className='generalStyles__width100'>
										<SelectLifts
											inputValue={description.point.lift_id}
											inputOnChange={onChangeLift}
											disabledInput={false}
											bankId={bankId}
										/>
									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
							<FormInputText
								inputValue={description.point.host_name}
								inputLabelName='Host name'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='host_name'
								isRequired
								disabledInput={false}

							/>
							<FormInputText
								inputValue={description.point.host_id}
								inputLabelName='Host ID'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='host_id'
								isRequired
								disabledInput={false}

							/>
							<FormInputText
								inputValue={description.point.group_name}
								inputLabelName='Group name'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='group_name'
								isRequired
								disabledInput={false}

							/>
							<FormInputText
								inputValue={description.point.group_id}
								inputLabelName='Group ID'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='group_id'
								isRequired
								disabledInput={false}

							/>
							<FormInputText
								inputValue={description.point.point_name}
								inputLabelName='Point name'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='point_name'
								isRequired
								disabledInput={false}

							/>
							<FormInputNumber
								inputValue={description.point.point_id}
								inputLabelName='Point ID'
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='point_id'

							/>
							<FormInputNumber
								inputValue={description.point.value}
								inputLabelName={t("general.value")}
								inputOnChange={onChangeFormValueDescription}
								inputVariableName='value'

							/>
						</div>
					</div>
					<br />
					{/* ======== LN2 Valued Message ======== */}
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<div className='col-md-12'>
								<span className='drawer__text' style={{ fontWeight: "bold" }}>
									LN2 Valued Message
								</span>
							</div>
							<FormSelectMessage
								inputValue={description.ln2_msg.msg.msg_id}
								inputOnChange={onChangeFormValueDescription}
								disabledInput={false}
								displaySelect
							/>
							<FormInputNumber
								inputValue={description.ln2_msg.msg.position}
								inputOnChange={onChangeFormValueDescription}
								inputLabelName={`LN2 ${t("general.position")}`}
								inputVariableName='position'

							/>
							<FormInputBooleanSwitch
								value={description.ln2_msg.msg.active}
								onChangeSwitch={onChangeFormValueDescription}
								labelName={`LN2 ${t("general.active")}`}
								variableName='active'
								isRequired
								disabledInput={false}

							/>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired' />
									</div>
									<div className='generalStyles__width100'>
										<div>
											<Button
												onClick={onClickAddNewDescription}
												className='buttonStyle__28 generalStyles__width100'
												disabled={!isDescriptionValid}
											>
												<FontAwesomeIcon
													icon={faPlus}
													className='generalStyles__mrFix'
												/>
												{t("general.add")}
											</Button>
										</div>

									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
					</div>
					<br />
					{/* ======== UPLOAD AND DOWNLOAD ======== */}
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							{/* Toggle: Use current bank */}
							<div className='col-md-12 mt-3'>
								<FormInputBooleanSwitch
									value={useCurrentBank}
									onChangeSwitch={(value: boolean) => setUseCurrentBank(value)}
									labelName='Use current bank'
									variableName='use_current_bank'
									isRequired={false}
									disabledInput={false}
								/>
								<div className='mt-2'>
									<p style={{ fontSize: '12px', color: '#888' }}>
										{useCurrentBank
											? 'When enabled, imported descriptions will use the current bank ID instead of the bank ID from the JSON file.'
											: 'When disabled, imported descriptions will keep the bank ID from the JSON file.'}
									</p>
								</div>
							</div>

							{/* Import Statistics */}
							{importStats && (
								<div style={{ padding: '15px', borderRadius: '5px' }}>
									<p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Import Statistics:</p>
									<div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
										<div>
											<span style={{ color: '#9cdcfe' }}>Total found: </span>
											<span style={{ color: '#b5cea8', fontWeight: 'bold' }}>{importStats.total}</span>
										</div>
										<div>
											<span style={{ color: '#9cdcfe' }}>Valid: </span>
											<span style={{ color: '#4ec9b0', fontWeight: 'bold' }}>{importStats.valid}</span>
										</div>
										<div>
											<span style={{ color: '#9cdcfe' }}>Invalid: </span>
											<span style={{ color: '#f48771', fontWeight: 'bold' }}>{importStats.invalid}</span>
										</div>
									</div>
								</div>
							)}

							<div className='col-md-12 mt-2'>
								<Button
									onClick={onClickUploadJsonFile}
									className='buttonStyle__28 generalStyles__width100'
								>
									<FontAwesomeIcon
										icon={faFileCode}
										className='generalStyles__mrFix'
									/>
									{t("general.uploadJson")}
								</Button>
							</div>
							<div className='col-md-12 mt-2'>
								<Button
									onClick={onClickDownloadJsonFile}
									className='buttonStyle__28 generalStyles__width100'
								>
									<FontAwesomeIcon
										icon={faDownload}
										className='generalStyles__mrFix'
									/>
									{t("general.downloadJsonFile")}
								</Button>
							</div>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
		</div >
	);
}
